/* Boarding School Games: the server, never aggregate storefront stock, decides eligibility. */
(() => {
  'use strict';
  if (customElements.get('bsg-rush-order')) return;

  const CHECK_MS = 2400;
  const ADD_MS = 5000;
  const cleanPath = (path) => path.replace(/\/+$/, '') || '/';
  const hash = (value) => {
    // Cart tokens and line keys stay out of sessionStorage, as do customer properties.
    let a = 2166136261;
    let b = 5381;
    for (let i = 0; i < value.length; i += 1) {
      a = Math.imul(a ^ value.charCodeAt(i), 16777619);
      b = Math.imul(b, 33) ^ value.charCodeAt(i);
    }
    return `${(a >>> 0).toString(16)}${(b >>> 0).toString(16)}`;
  };
  const fingerprint = (cart) => hash(JSON.stringify([
    cart.token || '', cart.currency,
    cart.items.map((item, index) => [String(item.variant_id), item.quantity,
      item.key || index, item.selling_plan_allocation?.selling_plan?.id || null])
  ]));
  const validCart = (cart) => cart && typeof cart.token === 'string' && cart.token.length > 0 && Array.isArray(cart.items) &&
    typeof cart.currency === 'string' && /^[A-Z]{3}$/.test(cart.currency) &&
    cart.items.every((item) => /^\d+$/.test(String(item.variant_id)) &&
      Number.isSafeInteger(item.quantity) && item.quantity > 0);

  class BsgRushOrder extends HTMLElement {
    connectedCallback() {
      if (this.listeners) return;
      this.dialog = this.querySelector('dialog');
      this.addButton = this.querySelector('[data-rush-add]');
      this.status = this.querySelector('[data-rush-status]');
      if (!this.dialog || !this.addButton || !this.status ||
          typeof this.dialog.showModal !== 'function' || !/^\d+$/.test(this.dataset.variantId || '')) return;
      try {
        this.root = new URL(this.dataset.rootUrl || '/', location.origin);
        this.cartUrl = new URL(this.dataset.cartUrl || 'cart', this.root);
        this.endpoint = new URL(this.dataset.endpoint, location.origin);
        if ([this.root, this.cartUrl, this.endpoint].some((url) => url.origin !== location.origin) ||
            !this.endpoint.pathname.startsWith('/apps/') ||
            !/^[a-z0-9-]+$/.test(this.dataset.productHandle || '')) return;
      } catch (_) { return; }
      this.state = 'idle';
      this.serial = (this.serial || 0) + 1;
      this.dismissed = new Set();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      document.addEventListener('click', this.onNavigationClick.bind(this), { ...options, capture: true });
      document.addEventListener('submit', this.onSubmit.bind(this), { ...options, capture: true });
      this.addButton.addEventListener('click', () => this.accept(), options);
      this.querySelectorAll('[data-rush-skip], [data-rush-close]').forEach((button) => {
        button.addEventListener('click', () => this.decline(), options);
      });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        if (this.state !== 'adding') this.decline();
      }, options);
      this.dialog.addEventListener('close', () => {
        if (this.state === 'open') this.decline();
      }, options);
      queueMicrotask(() => {
        if (this.isConnected && cleanPath(location.pathname) === cleanPath(this.cartUrl.pathname)) {
          this.offer({ kind: 'direct' });
        }
      });
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.listeners = null;
      this.operation?.abort();
      this.serial = (this.serial || 0) + 1;
      if (this.pending) this.finish(false);
    }

    ajax(path) { return new URL(path, this.root).href; }
    hasRush(cart) { return cart.items.some((item) => String(item.variant_id) === this.dataset.variantId); }
    writeKey(cart) { return `bsg-rush-order:pending:${hash(cart.token)}`; }

    quantityInputs() {
      return document.querySelectorAll('form[action*="/cart"] input[name="updates[]"], cart-items input[name="updates[]"], cart-drawer-items input[name="updates[]"]');
    }

    pendingEdits() {
      if (document.querySelector('.cart__items--disabled')) return true;
      for (const input of this.quantityInputs()) {
        if (input.value !== input.defaultValue) return true;
      }
      // Spotlight saves the note separately; let its normal checkout handle edited notes.
      return Array.from(document.querySelectorAll('textarea[name="note"]')).some((input) => input.value !== input.defaultValue);
    }

    cartInputsMismatch(cart) {
      // Compare the rendered values with the current server cart, separately
      // from locally typed edits. Replaying stale index-based updates[] after
      // another tab inserts/removes/reorders a line can change the wrong item.
      const groups = new Map();
      for (const input of this.quantityInputs()) {
        const owner = input.form || input.closest('cart-items, cart-drawer-items');
        if (!groups.has(owner)) groups.set(owner, []);
        groups.get(owner).push(input);
      }
      for (const inputs of groups.values()) {
        if (inputs.length !== cart.items.length) return true;
        for (const [position, input] of inputs.entries()) {
          const index = input.dataset.index ? Number(input.dataset.index) - 1 : position;
          const item = cart.items[index];
          if (!item || Number(input.defaultValue) !== item.quantity ||
              (input.dataset.quantityVariantId && input.dataset.quantityVariantId !== String(item.variant_id))) return true;
        }
      }
      return false;
    }

    onNavigationClick(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!link || this.contains(link) || link.hasAttribute('download') ||
          (link.target && link.target.toLowerCase() !== '_self')) return;
      let url;
      try { url = new URL(link.href, location.href); } catch (_) { return; }
      if (url.origin !== location.origin || cleanPath(url.pathname) !== cleanPath(this.cartUrl.pathname)) return;
      if (this.state !== 'idle' || this.pendingEdits()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.offer({ kind: 'link', url: url.href });
    }

    onSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const submitter = event.submitter;
      if (event.defaultPrevented || this.state !== 'idle' || !submitter || submitter.name !== 'checkout' ||
          (submitter.formTarget || form.target) && (submitter.formTarget || form.target).toLowerCase() !== '_self') return;
      const action = new URL(submitter.getAttribute('formaction') || form.action, location.href);
      if (action.origin !== location.origin || cleanPath(action.pathname) !== cleanPath(this.cartUrl.pathname) || this.pendingEdits()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.offer({ kind: 'checkout' });
    }

    async bounded(milliseconds, job) {
      const controller = new AbortController();
      this.operation = controller;
      let timer;
      try {
        return await Promise.race([
          job(controller.signal),
          new Promise((_, reject) => {
            timer = setTimeout(() => { controller.abort(); reject(new Error('timeout')); }, milliseconds);
          })
        ]);
      } finally {
        clearTimeout(timer);
        controller.abort();
        if (this.operation === controller) this.operation = null;
      }
    }

    async json(url, signal, body) {
      const response = await fetch(url, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal,
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {})
      });
      if (!response.ok) throw new Error('request_failed');
      return response.json();
    }

    async cart(signal) {
      const cart = await this.json(this.ajax('cart.js'), signal);
      if (!validCart(cart)) throw new Error('invalid_cart');
      return cart;
    }

    async product(signal) {
      const product = await this.json(this.ajax(`products/${this.dataset.productHandle}.js`), signal);
      const variant = product.variants?.find((item) => String(item.id) === this.dataset.variantId);
      if (!variant || variant.available !== true || !Number.isSafeInteger(variant.price) || variant.price < 0) throw new Error('rush_unavailable');
      return variant;
    }

    async eligible(cart, signal) {
      const response = await this.json(this.endpoint.href, signal, {
        items: cart.items.map((item) => ({ variant_id: String(item.variant_id), quantity: item.quantity }))
      });
      const checked = typeof response.checkedAt === 'number' ? response.checkedAt : Date.parse(response.checkedAt);
      return response.eligible === true && response.rushPresent === false && Number.isFinite(checked) &&
        Date.now() - checked <= 15000 && checked - Date.now() <= 60000;
    }

    wasDismissed(cart) {
      const key = `bsg-rush-order:v1:${hash(String(cart.token || 'anonymous'))}`;
      const value = fingerprint(cart);
      if (this.dismissed.has(`${key}:${value}`)) return true;
      try { return sessionStorage.getItem(key) === value; } catch (_) { return false; }
    }

    remember(cart) {
      if (!cart) return;
      const key = `bsg-rush-order:v1:${hash(String(cart.token || 'anonymous'))}`;
      const value = fingerprint(cart);
      this.dismissed.add(`${key}:${value}`);
      try { sessionStorage.setItem(key, value); } catch (_) { /* Private storage can be unavailable. */ }
    }

    price(variant, cart) {
      return new Intl.NumberFormat(document.documentElement.lang || navigator.language || 'en', {
        style: 'currency', currency: cart.currency
      }).format(variant.price / 100);
    }

    async offer(destination) {
      if (this.state !== 'idle' || this.pendingEdits()) return;
      this.pending = destination;
      this.state = 'checking';
      const serial = ++this.serial;
      try {
        // Without an origin-wide lock, concurrent tabs could each add the fee.
        if (typeof navigator.locks?.request !== 'function') { this.finish(false); return; }
        const checked = await this.bounded(CHECK_MS, async (signal) => {
          const [cart, variant] = await Promise.all([this.cart(signal), this.product(signal)]);
          if (this.cartInputsMismatch(cart)) return { reviewCart: true };
          if (!cart.items.length || this.hasRush(cart) || this.wasDismissed(cart) || this.pendingEdits() ||
              localStorage.getItem(this.writeKey(cart))) return null;
          if (!await this.eligible(cart, signal)) return null;
          return { cart, variant };
        });
        if (serial !== this.serial) return;
        if (checked?.reviewCart || checked && this.cartInputsMismatch(checked.cart)) { this.finish(false, true); return; }
        if (!checked || this.pendingEdits()) { this.finish(false); return; }
        this.offered = checked;
        this.querySelectorAll('[data-rush-price]').forEach((node) => { node.textContent = this.price(checked.variant, checked.cart); });
        this.status.textContent = '';
        this.setBusy(false);
        // Use the theme component's public API so its focus trap is also released.
        const notification = document.querySelector('cart-notification');
        if (notification && typeof notification.close === 'function') notification.close();
        this.dialog.showModal();
        this.state = 'open';
        this.addButton.focus();
      } catch (_) {
        if (serial === this.serial) this.finish(false);
      }
    }

    setBusy(busy) {
      this.dialog.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('[data-rush-add], [data-rush-skip], [data-rush-close]').forEach((button) => { button.disabled = busy; });
    }

    decline() {
      if (this.state !== 'open') return;
      this.remember(this.offered?.cart);
      this.finish(false);
    }

    async accept() {
      if (this.state !== 'open') return;
      this.state = 'adding';
      this.setBusy(true);
      this.status.textContent = 'Checking your cart and store availability…';
      const serial = ++this.serial;
      const controller = new AbortController();
      this.operation = controller;
      // Bound only time spent waiting for the lock. The request/add paths have
      // their own deadlines once the lock is acquired.
      const timer = setTimeout(() => controller.abort(), CHECK_MS);
      try {
        await navigator.locks.request(`bsg-rush-order:${hash(this.offered.cart.token)}`, {
          mode: 'exclusive', signal: controller.signal
        }, async () => {
          clearTimeout(timer);
          if (this.operation === controller) this.operation = null;
          if (serial === this.serial) await this.acceptWithLock(serial);
        });
      } catch (_) {
        if (serial === this.serial) this.finish(false);
      } finally {
        clearTimeout(timer);
        if (this.operation === controller) this.operation = null;
      }
    }

    async acceptWithLock(serial) {
      let submitted = false;
      let writeKey;
      try {
        const result = await this.bounded(CHECK_MS, async (signal) => {
          const [cart, variant] = await Promise.all([this.cart(signal), this.product(signal)]);
          if (this.cartInputsMismatch(cart)) return { reviewCart: true };
          if (this.pendingEdits()) return null;
          if (this.hasRush(cart)) return { present: true };
          if (localStorage.getItem(this.writeKey(cart))) return { uncertain: true };
          if (fingerprint(cart) !== fingerprint(this.offered.cart)) return { reviewCart: true };
          if (variant.price !== this.offered.variant.price || cart.currency !== this.offered.cart.currency) return { priceChanged: true, cart, variant };
          if (!await this.eligible(cart, signal)) return null;
          const latest = await this.cart(signal);
          if (this.cartInputsMismatch(latest)) return { reviewCart: true };
          if (this.pendingEdits()) return null;
          if (this.hasRush(latest)) return { present: true };
          if (fingerprint(latest) !== fingerprint(cart)) return { reviewCart: true };
          return { cart: latest };
        });
        if (serial !== this.serial) return;
        if (result?.present) { this.finish(true); return; }
        if (result?.uncertain || result?.reviewCart) { this.finish(false, true); return; }
        if (result?.priceChanged) {
          this.offered = result;
          this.querySelectorAll('[data-rush-price]').forEach((node) => { node.textContent = this.price(result.variant, result.cart); });
          this.status.textContent = 'The price changed. Please review the updated price before adding Rush.';
          this.state = 'open';
          this.setBusy(false);
          return;
        }
        if (!result) { this.finish(false); return; }
        this.status.textContent = 'Adding Rush My Order…';
        writeKey = this.writeKey(result.cart);
        // An aborted HTTP request can still commit after this tab releases the
        // lock. Keep a shared marker unless the result is positively confirmed.
        // If persistent storage is blocked, this throws before any cart write.
        localStorage.setItem(writeKey, '1');
        submitted = true;
        await this.bounded(ADD_MS, (signal) => this.json(this.ajax('cart/add.js'), signal, {
          items: [{ id: this.dataset.variantId, quantity: 1 }]
        }));
        localStorage.removeItem(writeKey);
        if (serial === this.serial) this.finish(true);
      } catch (_) {
        if (serial !== this.serial) return;
        if (!submitted) { this.finish(false); return; }
        // Aborting a request does not undo a server write. Never retry this POST.
        try {
          const cart = await this.bounded(CHECK_MS, (signal) => this.cart(signal));
          if (serial !== this.serial) return;
          if (this.hasRush(cart)) {
            localStorage.removeItem(writeKey);
            this.finish(true);
            return;
          }
        } catch (_) { /* The cart page is the recovery destination for uncertain writes. */ }
        if (serial === this.serial) this.finish(false, true);
      }
    }

    finish(added, reviewCart = false) {
      const destination = this.pending;
      this.pending = null;
      this.offered = null;
      this.state = 'idle';
      this.setBusy(false);
      if (this.dialog.open) this.dialog.close();
      if (!destination) return;
      if (reviewCart) { this.navigate(this.cartUrl.href); return; }
      if (destination.kind === 'direct') {
        if (added) this.navigate(this.cartUrl.href);
        return;
      }
      if (destination.kind === 'link') { this.navigate(destination.url); return; }
      // Never replay index-based updates[] after an asynchronous interception:
      // another tab may have reordered the cart even when the offer is declined.
      // Initial local edits are not intercepted at all. Edits appearing while
      // the offer is active require a fresh cart review before checkout.
      this.navigate(this.pendingEdits() ? this.cartUrl.href : this.ajax('checkout'));
    }

    navigate(url) { window.location.assign(url); }
  }

  customElements.define('bsg-rush-order', BsgRushOrder);
})();
